import React, { useState } from "react";
import { useLanguage } from '../contexts/LanguageContext';
import { useUser } from "../contexts/UserContext";
import { NewRequestModalProps } from "../types/Props";
import { useUserLocation } from "../utils/locationUtils";
import { isValidInput, isValidLength } from "../utils/validateInput";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { icons } from "../utils/icons";
import { getUserLocation } from "../api/user";
import { createRequest } from "../api/requests";
import Loader from '../components/Loader';
import FetchError from "../types/Error";
import { ModalWrapper } from "../components/ModalWrapper";
import { isUserAdmin } from "../utils/authUtils";

const NewRequestModal = ({ onClose, triggerRefresh }: NewRequestModalProps) => {
  const { translations } = useLanguage();
  const { user } = useUser();
  const [category, setCategory] = useState('');
  const [description, setDescription] = useState('');
  const [locationOption, setLocationOption] = useState('');
  const { userLatitude, userLongitude } = useUserLocation();
  const [isTest, setIsTest] = useState(false)
  const [error, setError] = useState('');
  const [descError, setDescError] = useState('');
  const [locError, setLocError] = useState('');
  const [loading, setLoading] = useState(false);
  const maxLength = 500;
  const descriptionPattern = /^[a-zA-ZäöÄÖ0-9\s.,!?:-]*$/;

  if (!user) return <Loader />;

  const toggleIsTest = () => {
    setIsTest(!isTest);
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (loading) return;

    setLoading(true);
    setError('');
    setLocError('');
    setDescError('');

    let isApproximate = false;

    if (locationOption === 'visible') {
      isApproximate = false;
    } else if (locationOption === 'approximate') {
      isApproximate = true;
    }

    // If user's location is not available, get it from the database
    let latitude = userLatitude;
    let longitude = userLongitude;
    if (latitude === null || longitude === null) {
      try {
        const data = await getUserLocation();
        latitude = data.latitude;
        longitude = data.longitude;
      } catch (error) {
        setError(translations.errors.genericerror);
        setLoading(false);
        return;
      }
    }

    // If user's location is still not available, show an error
    if (latitude === null || longitude === null) {
      setLocError(translations.errors.locationfailed);
      setLoading(false);
      return;
    };

    if (!isValidLength(description, 1, maxLength)) {
      setDescError(translations.errors.invaliddesclength);
      setLoading(false);
      return;
    };

    if (!isValidInput(description, descriptionPattern)) {
      setDescError(translations.errors.invalidcharacters);
      setLoading(false);
      return;
    };

    try {
      await createRequest({
        category,
        description,
        latitude,
        longitude,
        isApproximate,
        isTest,
      });
      
      triggerRefresh();
      onClose();
    } catch (error) {
      const err = error as FetchError;
      if (err.message === 'You have reached the daily request limit.') {
        setError(translations.errors.dailylimitreached);
      } else if (err.message === 'You have reached the limit of 2 active requests.') {
        setError(translations.errors.activelimitreached);
      } else {
        setError(translations.errors.genericerror);
      }
    }
    setLoading(false);
  };
  
  return (
    <ModalWrapper title={translations.newrequest} onClose={onClose}>
      <form className='modal-form' onSubmit={handleSubmit}>
        { isUserAdmin() && (
          <label className="checkbox-container">
            <input type="checkbox" name="isTest" checked={isTest} onChange={toggleIsTest} />
            <div className='checkmark show-all'></div>
            <p className='show-all-text'>Testipyyntö</p>
          </label>
        )}
        <p className="intertitle"><FontAwesomeIcon icon={icons.list} /> {translations.category}</p>
        <div className="select-wrapper">
          <select 
            name="category" 
            className="dropdown" 
            onChange={(e) => setCategory(e.target.value)}
            value={category}
            required
          >
            <option value="" disabled>{translations.choosecategory}</option>
            <option value="bicycle_maintenance">{translations.bicycle_maintenance}</option>
            <option value="food_and_hydration">{translations.food_and_hydration}</option>
            <option value="transportation_need">{translations.transportation_need}</option>
            <option value="other">{translations.other}</option>
          </select>
        </div>
        <p className="intertitle"><FontAwesomeIcon icon={icons.locationDot} /> {translations.location}</p>
        <div className="select-wrapper select-wrapper-location">
          <select 
            name="location" 
            className="dropdown" 
            onChange={(e) => setLocationOption(e.target.value)}
            value={locationOption}
            required
          >
            <option value="" disabled>{translations.chooselocation}</option>
            <option value="visible">{translations.showlocation}</option>
            <option value="approximate">{translations.showapproximate}</option>
          </select>
        </div>
        <p className="intertitle"><FontAwesomeIcon icon={icons.phone} /> {translations.phone}</p>
        <label>
          <input className="text-input phone-disabled" type="text" name="number" value={user.phone} disabled></input>
        </label>
        <p className="intertitle"><FontAwesomeIcon icon={icons.pen} /> {translations.description}</p>
        <div className="card">
          <textarea 
            name="description" 
            rows={4} 
            cols={40} 
            placeholder={translations.description} 
            maxLength={maxLength}
            onChange={(e) => setDescription(e.target.value)}
          />
          <div className="char-counter">
            {description.length}/{maxLength} {translations.characters}
          </div>
          {descError && <div className="error">{descError}</div>}
        </div>
        {error && <div className="error">{error}</div>}
        {locError && <div className="error">{locError}</div>}
        <button 
          type="submit" 
          className={`big-button send-request ${loading ? 'disabled' : ''}`}
          disabled={loading}
        >
          <FontAwesomeIcon icon={icons.share}/> {loading ? translations.loading : translations.submit}
        </button>
        <button type="button" className="big-button cancel" onClick={onClose}>
          <FontAwesomeIcon icon={icons.xmark}/> {translations.cancel}
        </button>
      </form>
    </ModalWrapper>
  );
}

export default NewRequestModal;